import { useEffect, useRef } from 'react';

import { useCartWithServerOrderSurcharges } from 'features/cart/hooks/use-cart-with-server-order-surcharges';
import Toast from 'react-native-toast-notifications';

import { IServerOrder } from '@rbi-ctg/menu';
import { modalToaster } from 'components/toaster/refs';
import { toaster } from 'components/toaster/toaster';
import { t } from 'state/intl/translations';
import { logger } from 'utils/logger';

export const OrderPriceChangeToast = ({ serverOrder }: { serverOrder: IServerOrder | null }) => {
  const toastMessage = t('restaurantPriceChangeToast');
  const { cartTotalCents } = useCartWithServerOrderSurcharges(serverOrder?.cart);
  const currentCartTotal = cartTotalCents ?? null;
  const orderId = serverOrder?.rbiOrderId ?? null;
  const storeNumber = serverOrder?.cart?.storeDetails?.storeNumber ?? null;
  const prevTotalCentsRef = useRef<number | null>(null);
  const prevStoreNumberRef = useRef<string | null>(null);

  useEffect(() => {
    logger.debug({
      message: '[OrderPriceChangeToast] effect',
      currentCartTotal,
      prevTotalCents: prevTotalCentsRef.current,
      orderId,
      storeNumber,
      prevStoreNumber: prevStoreNumberRef.current,
    });

    if (!orderId) {
      logger.debug({
        message: '[OrderPriceChangeToast] resetting - no orderId',
        currentCartTotal,
        prevTotalCents: prevTotalCentsRef.current,
        storeNumber,
        prevStoreNumber: prevStoreNumberRef.current,
      });
      prevTotalCentsRef.current = null;
      prevStoreNumberRef.current = null;
      return;
    }

    if (currentCartTotal === null) {
      logger.debug({
        message: '[OrderPriceChangeToast] skipping - no currentCartTotal',
        currentCartTotal,
        prevTotalCents: prevTotalCentsRef.current,
        orderId,
        storeNumber,
      });
      return;
    }

    if (storeNumber === null) {
      logger.debug({
        message: '[OrderPriceChangeToast] skipping - no storeNumber',
        currentCartTotal,
        prevTotalCents: prevTotalCentsRef.current,
        orderId,
        storeNumber,
      });
      return;
    }

    if (prevStoreNumberRef.current === null) {
      logger.debug({
        message: '[OrderPriceChangeToast] initializing store tracking',
        currentCartTotal,
        orderId,
        storeNumber,
      });
      prevStoreNumberRef.current = storeNumber;
      prevTotalCentsRef.current = currentCartTotal;
      return;
    }

    if (prevStoreNumberRef.current !== storeNumber) {
      if (currentCartTotal !== prevTotalCentsRef.current) {
        logger.info({
          message: '[OrderPriceChangeToast] store changed with total change - showing toast',
          previousTotalCents: prevTotalCentsRef.current,
          currentCartTotal,
          orderId,
          storeNumber,
          prevStoreNumber: prevStoreNumberRef.current,
        });
        toaster.positive(toastMessage);
      } else {
        logger.info({
          message: '[OrderPriceChangeToast] store changed without total change',
          currentCartTotal,
          prevTotalCents: prevTotalCentsRef.current,
          orderId,
          storeNumber,
          prevStoreNumber: prevStoreNumberRef.current,
        });
      }
      prevStoreNumberRef.current = storeNumber;
      prevTotalCentsRef.current = currentCartTotal;
      return;
    }

    if (currentCartTotal !== prevTotalCentsRef.current) {
      logger.debug({
        message: '[OrderPriceChangeToast] total changed without store change',
        previousTotalCents: prevTotalCentsRef.current,
        currentCartTotal,
        orderId,
        storeNumber,
      });
    } else {
      logger.debug({
        message: '[OrderPriceChangeToast] total unchanged',
        currentCartTotal,
        prevTotalCents: prevTotalCentsRef.current,
        orderId,
        storeNumber,
      });
    }
    prevTotalCentsRef.current = currentCartTotal;
  }, [currentCartTotal, orderId, storeNumber, toastMessage]);

  return (
    <Toast
      ref={x => {
        modalToaster.current = x;
      }}
    />
  );
};
